/**
 * 실시간 HOT 시설 랭킹 — 시설 페이지 CTR 훅 H3.
 *
 * 영구지침: docs/develop/frontend/home-page-portal-spec.md §4.2
 *
 * 데이터: /api/v1/facilities/hot-ranking (최근 행동 기반 점수 + 실제 순위변동)
 *         조회·전화·북마크·문의를 가중합해 15분 배치로 산정. 프리미엄은 순위에 개입하지 않음.
 * 차별화: PopularFacilities(북마크순)와 다른 지표. 랭크 강조 + 실제 변동 화살표.
 */
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Flame, ChevronRight, ArrowUp, ArrowDown, Minus } from 'lucide-react'
import apiClient from '@/lib/api/client'
import Skeleton from '@/components/ui/Skeleton'
import Badge from '@/components/ui/Badge'
import { FACILITY_TYPE_LABELS } from '@/types/common'
import type { FacilityType } from '@/types/common'
import {
  HOME_HOT_RANKING_QUERY_KEY,
  HOME_HOT_RANKING_PATH,
} from '@/features/home/widgetQueries'

interface HotRankingItem {
  id: number
  name: string
  facilityType: FacilityType
  addressSido: string
  rank: number
  delta: number | null
  deltaKind: 'up' | 'down' | 'flat' | 'new'
}

export default function HotRankingWidget() {
  // 쿼리 키·경로는 SSR 프리페치(entry-server.tsx)와 공유 — widgetQueries.ts
  const { data, isLoading } = useQuery({
    queryKey: HOME_HOT_RANKING_QUERY_KEY,
    queryFn: async () => {
      const res = await apiClient.get(HOME_HOT_RANKING_PATH)
      return res.data.data as HotRankingItem[]
    },
    staleTime: 5 * 60 * 1000,
  })

  return (
    <section className="rounded-2xl border border-cb-gray-100 bg-white p-5">
      <header className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-1.5 text-base font-bold text-cb-gray-900">
          <Flame size={16} className="text-rose-500" />
          실시간 HOT 시설
        </h2>
        <Link
          to="/facilities"
          className="flex items-center gap-0.5 text-xs font-medium text-cb-gray-500 hover:text-cb-primary"
        >
          전체 <ChevronRight size={12} />
        </Link>
      </header>

      {isLoading ? (
        <ul className="flex flex-col">
          {Array.from({ length: 5 }).map((_, i) => (
            <li key={i} className="border-b border-cb-gray-100 py-2 last:border-0">
              <Skeleton height="0.875rem" width="80%" />
            </li>
          ))}
        </ul>
      ) : (data?.length ?? 0) === 0 ? (
        <p className="py-4 text-center text-xs text-cb-gray-400">
          아직 노출할 시설이 없어요
        </p>
      ) : (
        <ol className="flex flex-col">
          {(data ?? []).slice(0, 5).map((f, idx) => (
            <li key={f.id} className="border-b border-cb-gray-100 last:border-0">
              <Link
                to={`/facilities/${f.id}`}
                className="flex items-center gap-2.5 py-2 hover:bg-cb-gray-50"
              >
                <span
                  className={`w-4 flex-shrink-0 text-center text-sm font-extrabold ${
                    idx < 3 ? 'text-rose-500' : 'text-cb-gray-400'
                  }`}
                >
                  {idx + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-cb-gray-900">
                    {f.name}
                  </p>
                  <p className="mt-0.5 flex items-center gap-1 text-[11px] text-cb-gray-500">
                    <Badge variant="type" className="text-[9px]">
                      {FACILITY_TYPE_LABELS[f.facilityType] ?? f.facilityType}
                    </Badge>
                    <span className="truncate">{f.addressSido}</span>
                  </p>
                </div>
                <RankDelta kind={f.deltaKind} delta={f.delta} />
              </Link>
            </li>
          ))}
        </ol>
      )}
    </section>
  )
}

function RankDelta({ kind, delta }: { kind: HotRankingItem['deltaKind']; delta: number | null }) {
  if (kind === 'up') {
    return (
      <span className="flex flex-shrink-0 items-center gap-0.5 text-[11px] font-bold text-rose-500">
        <ArrowUp size={11} />
        {Math.abs(delta ?? 0)}
      </span>
    )
  }
  if (kind === 'down') {
    return (
      <span className="flex flex-shrink-0 items-center gap-0.5 text-[11px] font-bold text-blue-500">
        <ArrowDown size={11} />
        {Math.abs(delta ?? 0)}
      </span>
    )
  }
  if (kind === 'new') {
    return (
      <span className="flex-shrink-0 rounded px-1 text-[9px] font-bold text-amber-600">
        NEW
      </span>
    )
  }
  return (
    <span className="flex flex-shrink-0 items-center text-[11px] font-bold text-cb-gray-300">
      <Minus size={11} />
    </span>
  )
}
